import {Suspense, useState} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import {useNavigate} from 'react-router';
import clsx from 'clsx';
import {Pagination} from 'swiper/modules';
import {Swiper, SwiperSlide} from 'swiper/react';
import {ERROR} from '@di-shared/const';
import {emitEventLog} from '@di-shared/features/analytics';
import ImageSelector from '@di-shared/features/common/ImageSelector';
import {t} from '@di-shared/features/common/useInferableTranslation';
import useIntersectionObserver from '@di-shared/features/common/useIntersectionObserver';
import MyMaintenanceGradeWarningLight from '@di-shared/features/diag/cpo/MyMaintenanceGradeWarningLight';
import useMaintenanceGrade, {
  AdditionalTextCase,
  InspectionStatus,
  MaintenanceGrade,
  RecallInfoCase,
} from '@di-shared/features/diag/cpo/useMaintenanceGrade';
import {ROUTE_PATHS} from '@di-shared/routes';
import {getDDayString} from '@di-shared/utils/dDay';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import {WebviewUtils} from '@shared/webviewUtils';

interface MyMaintenanceGradeProps {
  vin: string;
}

const MAX_COUNT_RECALL_INFOS = 3;
const VISIBLE_THRESHOLD = 0.7;

function linkToMaintenanceAppointment(vin: string) {
  WebviewUtils.openNewWebView({
    url: `${import.meta.env.VITE_CCI_DOMAIN}/maintenance/register/appointment?vin=${vin}`,
    isModal: true,
    isNavigationBar: true,
    isToolBar: false,
  });
}

function MyMaintenanceGrade({vin}: MyMaintenanceGradeProps) {
  const navigate = useNavigate();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const {maintenanceGrade, recallInfos, recallInfoCase, currentRoundEndDate, isButtonEnabled, additionalTextCase, inspections} =
    useMaintenanceGrade(vin);

  const [isMotionActive, setIsMotionActive] = useState<boolean>(false);
  const {ref} = useIntersectionObserver({
    visibleThreshold: VISIBLE_THRESHOLD,
    onVisible: () => setIsMotionActive(true),
    isAlwaysExcute: false,
  });

  return (
    <section className="section section_certification_management">
      <div className="section_container_header">
        <h3 className="title_medium_b">{t('T20225')}</h3>
        <div className="my_grade">
          {(maintenanceGrade === MaintenanceGrade.S ||
            maintenanceGrade === MaintenanceGrade.A ||
            maintenanceGrade === MaintenanceGrade.B) && (
            <span
              className={clsx('ic ic_certification_grade', {
                grade_s: maintenanceGrade === MaintenanceGrade.S,
                grade_a: maintenanceGrade === MaintenanceGrade.A,
                grade_b: maintenanceGrade === MaintenanceGrade.B,
              })}
            >
              <span className="blind">{t('T20218_$grade', {grade: maintenanceGrade})}</span>
            </span>
          )}
          {maintenanceGrade === MaintenanceGrade.E && <span className="my_grade_text">{t('T20080')}</span>}
        </div>
      </div>
      <ErrorBoundary fallback={<></>}>
        <Suspense>
          <MyMaintenanceGradeWarningLight vin={vin} />
        </Suspense>
      </ErrorBoundary>
      <div
        className="certification_management_wrap"
        ref={ref}
      >
        <Swiper
          modules={[Pagination]}
          className={clsx('certification-management-swiper', {
            ['is-active']: isMotionActive,
          })}
          pagination={{el: '.swiper-pagination', type: 'bullets'}}
        >
          <SwiperSlide>
            <div className="section_container">
              {recallInfoCase === RecallInfoCase.HAS_RECALL_INFO ? (
                <>
                  <div className="certification_management_header">
                    <strong
                      className="certification_management_title"
                      style={{whiteSpace: 'pre-wrap'}}
                    >
                      {t('T30367')}
                    </strong>
                    <span className="certification_management_count">{t('T30368_$n', {n: recallInfos.length})}</span>
                  </div>
                  <div className="certification_management_content">
                    {recallInfos.slice(0, MAX_COUNT_RECALL_INFOS).map(({occurrenceDate, inspectionDetail}) => (
                      <div
                        className="inspect_require_card"
                        key={occurrenceDate + inspectionDetail}
                      >
                        <div className="inspect_require_head">
                          <span className="inspect_require_date">
                            {t('T30369_$date', {date: occurrenceDate ? CldrDatetimeFormatter.format(occurrenceDate, 'yMMMd') : ERROR.DASH})}
                          </span>
                          <div className="badge_box">
                            <span className="badge badge_container_red badge_20px">{t('T20080')}</span>
                          </div>
                        </div>
                        <strong
                          className="inspect_require_title"
                          style={{whiteSpace: 'pre-wrap'}}
                        >
                          {inspectionDetail}
                        </strong>
                      </div>
                    ))}
                    <p className="notice_refer">{t('T30370')}</p>
                  </div>
                  <div className="certification_management_footer">
                    <div className="btn_box btn_box_complex">
                      <button
                        type="button"
                        className="btn btn_large btn_outline"
                        onClick={() => {
                          emitEventLog({
                            event: 'di_vehicle_management',
                            data_action: 'view certification history',
                            data_name: t('T30371'),
                          });
                          navigate(ROUTE_PATHS.CPO_RECALL.getFullPath(vin));
                        }}
                      >
                        {t('T30371')}
                      </button>

                      <button
                        type="button"
                        className="btn btn_large btn_filled type_primary"
                        onClick={() => linkToMaintenanceAppointment(vin)}
                      >
                        {t('T30372')}
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="certification_management_header">
                    <span className="certification_management_label">{t('T30373')}</span>
                    <strong
                      className="certification_management_title"
                      style={{whiteSpace: 'pre-wrap'}}
                    >
                      {t('T30385')}
                    </strong>
                  </div>
                  <div className="certification_management_content">
                    <div className="empty_area">
                      <div className="empty_img">
                        {/* * [2025-02-12:Jeyoung Lee] FIXME: 기아에는 없는 기능이라 이미지 아무거나 넣어놓음*/}
                        <ImageSelector
                          kiaLightSrc={() => import('@di-shared/assets/img/visual/hyundai/img_visual_hyundai_certification_empty.png')}
                          kiaDarkSrc={() => import('@di-shared/assets/img/visual/hyundai/img_visual_hyundai_certification_empty.png')}
                          hyundaiLightSrc={() => import('@di-shared/assets/img/visual/hyundai/img_visual_hyundai_certification_empty.png')}
                          hyundaiDarkSrc={() => import('@di-shared/assets/img/visual/hyundai/img_visual_hyundai_certification_empty.png')}
                          genesisLightSrc={() => import('@di-shared/assets/img/visual/genesis/img_visual_genesis_certification_empty.png')}
                          genesisDarkSrc={() => import('@di-shared/assets/img/visual/genesis/img_visual_genesis_certification_empty.png')}
                          width={120}
                          height={120}
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </SwiperSlide>
          <SwiperSlide>
            <div className="section_container">
              <div className="certification_management_header">
                <strong className="certification_management_title">
                  {t('T30374_$date', {date: currentRoundEndDate ? CldrDatetimeFormatter.format(currentRoundEndDate, 'yMMMd') : ERROR.DASH})}
                </strong>
                {additionalTextCase === AdditionalTextCase.TRANSFERRED ? (
                  <p className="certification_management_desc">{t('T30375')}</p>
                ) : AdditionalTextCase.EXPIRED ? (
                  <p className="certification_management_desc">{t('T30376')}</p>
                ) : null}
              </div>
              <div className="certification_management_content">
                <div className="inspect_regular_grid">
                  <ul className="inspect_regular_list">
                    {inspections.map(({round, status, dday}) =>
                      status === InspectionStatus.COMPLETE ? (
                        <InspectRegularComplete
                          round={round}
                          key={round}
                        />
                      ) : status === InspectionStatus.READY ? (
                        <InspectRegularReady
                          round={round}
                          dDay={dday}
                          key={round}
                        />
                      ) : status === InspectionStatus.EXPIRED ? (
                        <InspectRegularExpired
                          round={round}
                          key={round}
                        />
                      ) : (
                        <InspectRegularInvalid
                          round={round}
                          key={round}
                        />
                      )
                    )}
                  </ul>
                </div>
              </div>
              <div className="certification_management_footer">
                {isButtonEnabled && (
                  <button
                    type="button"
                    className="btn btn_large btn_filled btn_full type_primary"
                    onClick={() => {
                      emitEventLog({
                        event: 'di_vehicle_management',
                        data_action: 'book inspection',
                        data_name: t('T30377'),
                      });
                      linkToMaintenanceAppointment(vin);
                    }}
                  >
                    {t('T30377')}
                  </button>
                )}
              </div>
            </div>
          </SwiperSlide>
          <div className="swiper-pagination" />
        </Swiper>
      </div>
    </section>
  );
}

export default MyMaintenanceGrade;

function InspectRegularComplete({round}: {round: number}) {
  return (
    <li className="inspect_regular_item is-complete">
      <span className="inspect_regular_count">{t('T30380_$round', {round})}</span>
      <span className="inspect_regular_text">{t('T30381')}</span>
    </li>
  );
}

function InspectRegularReady({round, dDay}: {round: number; dDay: number}) {
  return (
    <li className="inspect_regular_item is-active">
      <span className="inspect_regular_count">{t('T30380_$round', {round})}</span>
      {dDay === 0 ? (
        <span className="inspect_regular_date">{t('T10008_#en')}</span>
      ) : (
        <span className="inspect_regular_date">{getDDayString(-dDay)}</span>
      )}
    </li>
  );
}

function InspectRegularExpired({round}: {round: number}) {
  return (
    <li className="inspect_regular_item">
      <span className="inspect_regular_count">{t('T30380_$round', {round})}</span>
      <span className="inspect_regular_text">{t('T30382')}</span>
    </li>
  );
}

function InspectRegularInvalid({round}: {round: number}) {
  return (
    <li className="inspect_regular_item">
      <span className="inspect_regular_count">{t('T30380_$round', {round})}</span>
    </li>
  );
}
